import React, {useState, useEffect, useRef, useMemo, ReactElement} from 'react';
import Box from '@mui/material/Box';
import {generateProgressBarStripes} from './utills';
import BarStripe from './BarStripe';
import '../../../styles/components/common/_aur-progress-chart.scss';

// Define interface for the AurProgressChart component props
export interface ProgressChartDataSetType {
  total: number;
  progress: number;
  filledColor: string;
  unfilledColor?: string;
  key: string;
  tooltip?: {
    filled?: string | ReactElement;
    unFilled?: string | ReactElement;
  };
}

interface ProgressChartProps {
  dataSet: ProgressChartDataSetType[];
  layout?: {
    barWidth: number;
    barHeight: number;
    spaceBetweenBar: number;
  };
  fixedWidth?: number;
}

const AurProgressChart: React.FC<ProgressChartProps> = ({
  dataSet,
  layout = {
    barWidth: 5.2,
    spaceBetweenBar: 1,
    barHeight: 48,
  },
  fixedWidth,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const handleWindowResizeEvent = () => {
      // Update container's width on window resize event and regenerate progress chart
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth); // Get the actual width of the parent container
      }
    };

    // Set initial width
    handleWindowResizeEvent();

    // Add event listener for window resize event listen
    window.addEventListener('resize', handleWindowResizeEvent);

    return () => {
      window.removeEventListener('resize', handleWindowResizeEvent);
    };
  }, []);

  const chartDataStripes = useMemo(() => {
    return generateProgressBarStripes(
      dataSet,
      fixedWidth ?? containerWidth,
      layout
    );
  }, [dataSet, containerWidth, fixedWidth]);

  return (
    <Box
      className="progress-chart-container"
      ref={containerRef}
      role="progressbar"
      aria-label="Progress Bar chart"
    >
      {chartDataStripes.map((stripe, index) => (
        <BarStripe
          {...stripe}
          key={`stripe-bar-${index}`}
          aria-labelledby={`bar-stripe-${index}`}
        />
      ))}
    </Box>
  );
};

export default AurProgressChart;
